/**
 * UPT-2079 L5d (web §11.5, §11.7) — logique PURE du bloc « Dépôt dans git », sans React ni réseau :
 * - `repoDepositState` : l'état du bouton (sans droit, indisponible avec sa raison, disponible avec sa cible) ; sans droit, « pas le
 *   droit » prime sur l'indisponibilité (une seule explication à la fois) ;
 * - `isAzureBranchUrl` : l'URL d'une branche n'est un lien que si elle respecte `GENERATOR_REPO.branchUrlPattern` (HTTPS,
 *   `dev.azure.com`, dépôt `Geode-module-…`, `?version=GB…`) : c'est l'UNIQUE valeur du serveur qui devient un `href` dans ce web
 *   (exception au §6 du lot, testée avec des entrées hostiles) ;
 * - `shortCommit` : les huit premiers caractères d'un identifiant de commit ;
 * - `repoReasonToken` : le jeton de raison d'un refus, seul `message` du serveur affiché (en `<code dir="ltr">`).
 * Les libellés vivent dans `generatorI18n.ts` (`generatorRepoUnavailableId`, `generatorRepoFailedId`).
 */
import { GENERATOR_REPO, type GeneratorRepoStatus, type GeneratorRepoStatusReason, type GeneratorRepoTarget } from '../data/contracts';

/** Longueur maximale d'une URL examinée : au-delà, ce n'est pas une URL de branche (l'expression régulière n'est pas lancée). */
const BRANCH_URL_MAX_LENGTH = 2048;

/** Nombre de caractères d'un identifiant de commit rendu à l'écran. */
const SHORT_COMMIT_LENGTH = 8;

/** Jeton de raison attendu du serveur (`repo-name`, `credential-name`, `not-text`, …) : minuscules, chiffres et tirets. */
const REASON_TOKEN = /^[a-z][a-z0-9-]{0,31}$/;

/** Vrai quand `url` est une chaîne qui respecte `GENERATOR_REPO.branchUrlPattern` ; `null`, un autre type, `javascript:`, `http:` ou un autre hôte → faux. */
export const isAzureBranchUrl = (url: unknown): url is string =>
  typeof url === 'string' && url.length <= BRANCH_URL_MAX_LENGTH && GENERATOR_REPO.branchUrlPattern.test(url);

/** Lien d'organisation dérivé seulement d'une URL Azure canonique du dépôt attendu. */
export const repoCreationUrl = (repoUrl: unknown, moduleCode: string): string | null => {
  if (typeof repoUrl !== 'string' || repoUrl.length > BRANCH_URL_MAX_LENGTH) return null;
  const match = /^https:\/\/dev\.azure\.com\/([A-Za-z0-9_-]+)\/([A-Za-z0-9_-]+)\/_git\/(Geode-module-[A-Za-z0-9_-]+)$/.exec(repoUrl);
  return match && match[3] === `Geode-module-${moduleCode}` ? `https://dev.azure.com/${match[1]}` : null;
};

/** Les huit premiers caractères d'un identifiant de commit (chaîne vide pour une valeur qui n'est pas une chaîne). */
export const shortCommit = (commit: unknown): string => (typeof commit === 'string' ? commit.slice(0, SHORT_COMMIT_LENGTH) : '');

/** Jeton de raison affichable ; toute autre valeur (absente, trop longue, avec des caractères inattendus) → `?`. */
export const repoReasonToken = (message: string | null | undefined): string =>
  typeof message === 'string' && REASON_TOKEN.test(message) ? message : '?';

/** Ce que le bloc montre sous le bouton « Déposer dans le dépôt du module ». */
export type RepoDepositState =
  | { kind: 'no-right' }
  | { kind: 'unavailable'; reason: GeneratorRepoStatusReason | null }
  | { kind: 'available'; target: GeneratorRepoTarget };

/**
 * État du bouton : sans le droit UPDATE → `no-right` (prime sur le reste) ; dépôt indisponible → `unavailable` avec sa raison ;
 * sinon `available` avec la cible. Un dépôt annoncé disponible sans cible est traité comme indisponible (raison inconnue).
 */
export const repoDepositState = (status: GeneratorRepoStatus, canDeposit: boolean): RepoDepositState => {
  if (!canDeposit) return { kind: 'no-right' };
  if (status.available && status.target) return { kind: 'available', target: status.target };
  return { kind: 'unavailable', reason: status.available ? null : status.reason };
};


export const isAzurePullRequestUrl = (url: unknown): url is string => typeof url === 'string' && url.length <= 2048
  && new RegExp('^https://dev[.]azure[.]com/[A-Za-z0-9_.-]+/[A-Za-z0-9_.-]+/_git/Geode-module-[A-Za-z0-9_-]+/pullrequest/[1-9][0-9]*$').test(url);
